// /auth/callback — web SSO callback handler.
//
// Web SSO Exchange flow: backend `/api/auth/sso-exchange/callback` сам делает
// redirect на эту страницу (или сразу на `/legal/accept` / `/onboarding` / `/`).
// Эта страница нужна как fallback, если редирект пришёл с `?error=...` или
// мы попали сюда из Universal Link на iOS (Capacitor — fallback).
import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { IconLoader2, IconSparkles } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const searchSchema = z.object({
	error: z.string().optional(),
	redirectTo: z.string().optional(),
});

export const Route = createFileRoute("/auth/callback")({
	validateSearch: tolerantSearch(searchSchema),
	component: AuthCallbackPage,
});

function AuthCallbackPage(): React.ReactElement {
	const { t } = useTranslation("auth");
	const search = Route.useSearch();
	const navigate = useNavigate();

	useEffect(() => {
		if (search.error) {
			void navigate({ to: "/auth/login", search: { error: search.error } });
			return;
		}
		// Если backend сюда вернул без ошибки — редирект на dashboard или указанный путь.
		void navigate({ to: search.redirectTo ?? "/", replace: true });
	}, [search.error, search.redirectTo, navigate]);

	return (
		<div className="flex min-h-dvh flex-col items-center justify-center gap-5 bg-background px-6">
			<div className="flex items-center gap-2 text-lg font-semibold tracking-tight">
				<IconSparkles size={22} stroke={1.75} className="text-primary" />
				StudyHub
			</div>
			<div className="flex size-12 items-center justify-center rounded-[var(--radius-tile)] bg-primary/10 shadow-e1">
				<IconLoader2 size={24} className="animate-spin text-primary" stroke={1.75} />
			</div>
			<p className="text-sm text-muted-foreground">{t("callback.finishing")}</p>
		</div>
	);
}
